import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { keys } from "lodash";

import {
  BAND_ACTIONS,
  IPeopleBlockParams,
  IEventPeopleBlockParams,
  PeopleBlockStatus,
} from "Bands/types";
import { EventPeopleBlockService } from "Bands/services";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";

export const dispatchGetEventPeopleBlocks = (dispatch: Dispatch<any>, event?: string) => {
  dispatch(
    getEventPeopleBlocks({
      event,
      page: 1, // we assume all blocks will be returned in page 1
      itemsPerPage: 100, // avoid pagination
    })
  );
};

export const getEventPeopleBlocks = createAsyncThunk(
  BAND_ACTIONS.getEventPeopleBlocks,
  async (
    {
      event,
      itemsPerPage,
      page,
    }: {
      event?: string;
      page: number;
      itemsPerPage: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventPeopleBlockService.getBlocks({
        event: event ? API_ENDPOINTS.EVENT(event) : undefined,
        page,
        itemsPerPage,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventPeopleBlock]: data.eventPeopleBlock,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventPeopleBlock = (dispatch: Dispatch<any>, blockId: string) => {
  const action = API_ENDPOINTS.EVENT_PEOPLE_BLOCK(blockId);
  return dispatch(getEventPeopleBlock({ blockId, action }));
};

export const getEventPeopleBlock = createAsyncThunk(
  BAND_ACTIONS.getEventPeopleBlock,
  async ({ blockId }: { blockId: string; action: string }, { rejectWithValue }) => {
    try {
      const block = await EventPeopleBlockService.getBlock(blockId);

      return block;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventPeopleBlock = createAsyncThunk(
  BAND_ACTIONS.createEventPeopleBlock,
  async (block: IEventPeopleBlockParams, { rejectWithValue }) => {
    try {
      const data = await EventPeopleBlockService.createBlock(block);
      const id = Object.values(data?.eventPeopleBlock)?.[0]?.attributes?.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventPeopleBlock = createAsyncThunk(
  BAND_ACTIONS.editEventPeopleBlock,
  async (props: { id: string; data: IEventPeopleBlockParams }, { rejectWithValue }) => {
    try {
      const data = await EventPeopleBlockService.editBlock(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventPeopleBlockTitle = createAsyncThunk(
  BAND_ACTIONS.editEventPeopleBlockTitle,
  async (props: { id: string; title: string }, { rejectWithValue }) => {
    try {
      const data = await EventPeopleBlockService.editBlockTitle(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventPeopleBlock = createAsyncThunk(
  BAND_ACTIONS.deleteEventPeopleBlock,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventPeopleBlockService.deleteBlock(id);
      return {
        path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK(id),
        dataKey: IDataKeys.eventPeopleBlock,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addMembersToPeopleBlock = createAsyncThunk(
  BAND_ACTIONS.addMembersToPeopleBlock,
  async (props: { id: string; data: IPeopleBlockParams }, { rejectWithValue }) => {
    try {
      const data = await EventPeopleBlockService.addMembers(props);

      const key = keys(data.eventPeopleBlock)[0];
      const createdItem = data.eventPeopleBlock?.[key];

      return { id: props.id, data, createdItem };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editPeopleBlockUser = createAsyncThunk(
  BAND_ACTIONS.editPeopleBlockUser,
  async (props: { id: string; data: IPeopleBlockParams }, { rejectWithValue }) => {
    try {
      const data = await EventPeopleBlockService.editUser(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deletePeopleBlockUser = createAsyncThunk(
  BAND_ACTIONS.deletePeopleBlockUser,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventPeopleBlockService.deleteUser(id);
      return {
        path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK_USER(id),
        dataKey: IDataKeys.eventPeopleBlockUser,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventRSVP = (dispatch: Dispatch<any>, rsvpId: string) => {
  const action = API_ENDPOINTS.EVENT_RSVP(rsvpId);
  return dispatch(getEventRSVP({ rsvpId, action }));
};

export const getEventRSVP = createAsyncThunk(
  BAND_ACTIONS.getEventPeopleBlock,
  async ({ rsvpId }: { rsvpId: string; action: string }, { rejectWithValue }) => {
    try {
      const rsvp = await EventPeopleBlockService.getRSVP(rsvpId);

      return rsvp;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const answerEventRSVP = createAsyncThunk(
  BAND_ACTIONS.answerEventRSVP,
  async ({ id, status }: { id: string; status: PeopleBlockStatus }, { rejectWithValue }) => {
    try {
      await EventPeopleBlockService.answerRSVP({
        id,
        status,
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
